import { Component, OnInit, OnDestroy, Inject } from '@angular/core';
import { Router } from '@angular/router';
import {
    MsalService,
    MsalBroadcastService,
    MSAL_GUARD_CONFIG,
    MsalGuardConfiguration,
} from '@azure/msal-angular';
import {
    EventMessage,
    EventType,
    InteractionStatus,
    PopupRequest,
    RedirectRequest,
} from '@azure/msal-browser';
import { RoutingPathsTree } from '@core/constants/routing-paths-tree.const';
import { PermissionsService } from '@core/services/permissions.service';
import { UsersService } from '@core/services/users.service';
import { BehaviorSubject, Subject } from 'rxjs';
import { filter, finalize, skip, takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit, OnDestroy {
    private readonly _destroying$ = new Subject<void>();
    isLogin: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    isIframe: boolean = false;
    loginStarted: boolean = false;

    constructor(
        @Inject(MSAL_GUARD_CONFIG)
        private msalGuardConfig: MsalGuardConfiguration,
        private broadcastService: MsalBroadcastService,
        private authService: MsalService,
        private router: Router,
        private usersService: UsersService,
        private permissionsService: PermissionsService
    ) {}

    ngOnInit() {
        this.isIframe = window !== window.parent && !window.opener;

        this.authService.instance.enableAccountStorageEvents(); // Optional - This will enable ACCOUNT_ADDED and ACCOUNT_REMOVED events emitted when a user logs in or out of another tab or window
        this.broadcastService.msalSubject$
            .pipe(
                filter(
                    (msg: EventMessage) =>
                        msg.eventType === EventType.ACCOUNT_ADDED ||
                        msg.eventType === EventType.ACCOUNT_REMOVED
                )
            )
            .subscribe((result: EventMessage) => {
                if (this.authService.instance.getAllAccounts().length === 0) {
                    window.location.pathname = '/';
                } else {
                    this.setLoginDisplay();
                }
            });

        this.broadcastService.inProgress$
            .pipe(
                filter(
                    (status: InteractionStatus) =>
                        status === InteractionStatus.None
                ),
                takeUntil(this._destroying$)
            )
            .subscribe(() => {
                this.setLoginDisplay();
                this.checkAndSetActiveAccount();
            });

        // this.broadcastService.msalSubject$
        //     .pipe(
        //         filter(
        //             (msg: EventMessage) =>
        //                 msg.eventType === EventType.LOGIN_SUCCESS
        //         )
        //     )
        //     .subscribe((result: EventMessage) => {
        //         console.log(result);
        //     });

        this.isLogin
            .asObservable()
            .pipe(takeUntil(this._destroying$), skip(1))
            .subscribe((value: boolean) => {
                if (!value && !this.loginStarted) {
                    this.login();
                }
            });
    }

    checkAndSetActiveAccount() {
        /**
         * If no active account set but there are accounts signed in, sets first account to active account
         * To use active account set here, subscribe to inProgress$ first in your component
         * Note: Basic usage demonstrated. Your app may require more complicated account selection logic
         */
        let activeAccount = this.authService.instance.getActiveAccount();

        if (
            !activeAccount &&
            this.authService.instance.getAllAccounts().length > 0
        ) {
            let accounts = this.authService.instance.getAllAccounts();
            this.authService.instance.setActiveAccount(accounts[0]);
        }
    }

    login() {
        this.loginStarted = true;
        if (this.msalGuardConfig.authRequest) {
            this.authService
                .loginRedirect({
                    ...this.msalGuardConfig.authRequest,
                } as RedirectRequest)
                .pipe(
                    finalize(() => {
                        this.loginStarted = false;
                        this.setLoginDisplay();
                    })
                )
                .subscribe({
                    next: () => {
                        this.router.navigate([RoutingPathsTree.Home.route]);
                    },
                    error: (error) => {
                        console.log(error);
                    },
                });
        } else {
            this.authService
                .loginRedirect()
                .pipe(
                    finalize(() => {
                        this.loginStarted = false;
                        this.setLoginDisplay();
                    })
                )
                .subscribe({
                    next: () => {
                        this.router.navigate([RoutingPathsTree.Home.route]);
                    },
                    error: (error) => {
                        console.log(error);
                    },
                });
        }
    }

    // logout() {
    //   // Add log out function here
    //   this.authService.logoutPopup({
    //     mainWindowRedirectUri: '/',
    //   });
    // }

    setLoginDisplay() {
        const condition = !!(
            this.authService.instance?.getAllAccounts()?.length > 0
        );
        if (condition) {
            this.permissionsService.getPermissions();
        } else {
            this.usersService.setCurrentUser(null);
            this.permissionsService.resetPermissions();
        }
        this.isLogin.next(condition);
    }

    ngOnDestroy(): void {
        this._destroying$.next(undefined);
        this._destroying$.complete();
    }
}
